/** Engineer view of the shared authoring board. */
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { Avatar } from '@/components/Avatar'
import { Icon } from '@/components/Icon'
import { MessageComposer, MessageSendButton } from '@/components/MessageComposer'
import { AgentIdentity, AgentStatus } from '@/components/agent/AgentJobs'
import type { BoardMessage, BoardThread } from '@/components/agent/board'
import type { BoardJob } from '@/components/agent/types'
import { fmtDateTime } from '@/lib/format'
import './agents.css'
import './agent-messages.css'

type Page<T> = { items: T[]; next_cursor: string | null }

function CurrentStatus({ taskId, statuses }: { taskId: string; statuses: Map<string, string> }) {
  const status = statuses.get(taskId)
  return status ? <AgentStatus status={status} /> : <span className="agent-status">Status unavailable</span>
}

function AgentProfile({ message, statuses, monitorPaths }: {
  message: BoardMessage; statuses: Map<string, string>; monitorPaths: Map<string, string>
}) {
  return <Link className="agent-profile-link"
    to={monitorPaths.get(message.task_id) ?? `/eng/agents?agent=${encodeURIComponent(message.task_id)}`}
    aria-label={`Open ${message.author}'s monitor`}>
    <AgentIdentity identity={message.task_id} name={message.author} program={message.program}
      running={statuses.get(message.task_id) === 'running'} />
  </Link>
}

function Discussion({ value, statuses, monitorPaths, onCorrected, onDeleted, focused, linkedReply }: {
  value: BoardThread; statuses: Map<string, string>; monitorPaths: Map<string, string>; focused: boolean
  linkedReply?: BoardMessage | null
  onCorrected: (thread: BoardThread) => void; onDeleted: (id: string) => void
}) {
  const [history, setHistory] = useState<BoardMessage[] | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function loadHistory(next?: string) {
    setBusy(true)
    setError('')
    try {
      const page = await api.get<Page<BoardMessage>>(`/eng/agent-messages/${encodeURIComponent(value.id)}/replies`,
        { limit: 20, ...(next ? { cursor: next } : {}) })
      setHistory(old => next ? [...(old ?? []), ...page.items] : page.items)
      setCursor(page.next_cursor)
    } catch (caught) { setError((caught as Error).message) }
    finally { setBusy(false) }
  }

  async function correct() {
    if (!text.trim()) return
    setBusy(true)
    setError('')
    try {
      const result = await api.post<{ thread: BoardThread }>(
        `/eng/agent-messages/${encodeURIComponent(value.id)}/correct`, { message: text })
      onCorrected(result.thread)
      setHistory(null)
      setCursor(null)
      setEditing(false)
      setText('')
    } catch (caught) { setError((caught as Error).message) }
    finally { setBusy(false) }
  }

  async function deletePost() {
    setBusy(true)
    setError('')
    try {
      await api.del(`/eng/agent-messages/${encodeURIComponent(value.id)}`)
      onDeleted(value.id)
    } catch (caught) { setError((caught as Error).message) }
    finally { setBusy(false) }
  }

  const replies = (history ?? value.replies).filter(reply => reply.kind === 'reply')
  return <article id={`thread-${value.id}`} className={`agent-message-card experiment-card${focused ? ' is-focused' : ''}`}>
    <header className="agent-message-head">
      <AgentProfile message={value} statuses={statuses} monitorPaths={monitorPaths} />
      <CurrentStatus taskId={value.task_id} statuses={statuses} />
    </header>
    <p className="agent-message-content">{value.content}</p>
    {value.correction && <aside className="agent-message-correction">
      <Avatar name={value.correction.author} size={30} />
      <div><strong>Engineer correction · {value.correction.author}</strong>
        <p>{value.correction.content}</p>
        <time dateTime={value.correction.time_posted}>{fmtDateTime(value.correction.time_posted)}</time>
      </div>
    </aside>}
    {linkedReply && (!expanded || !replies.some(reply => reply.id === linkedReply.id)) &&
      <aside className="agent-message-linked-reply">
        <span>Linked reply</span>
        <div className="agent-message-reply-head">
          <AgentProfile message={linkedReply} statuses={statuses} monitorPaths={monitorPaths} />
          <CurrentStatus taskId={linkedReply.task_id} statuses={statuses} />
        </div>
        <p>{linkedReply.content}</p>
        <time dateTime={linkedReply.time_posted}>{fmtDateTime(linkedReply.time_posted)}</time>
      </aside>}
    <footer className="agent-message-footer">
      <div className="agent-message-meta">
        <Link to={`/eng/tasks/${value.task_id}`} title={`Open task ${value.task_id}`}>Open task</Link>
        <span aria-hidden="true">·</span>
        <time dateTime={value.time_posted}>{fmtDateTime(value.time_posted)}</time>
        {value.superseded && <span className="agent-status warn">Superseded</span>}
      </div>
      <div className="agent-message-actions">
        <button type="button" className="agent-replies-toggle" aria-expanded={expanded}
          aria-controls={`replies-${value.id}`} onClick={() => setExpanded(open => !open)}>
          <Icon name="chat" size={15} /><span>{value.replies.length ? `${value.replies.length} recent ${value.replies.length === 1 ? 'reply' : 'replies'}` : 'Replies'}</span>
          <Icon name="chevron" size={15} style={{ transform: expanded ? 'rotate(180deg)' : undefined }} />
        </button>
        <div className="agent-message-actions-right">
          {!editing && <button className="link" onClick={() => { setEditing(true); setConfirmDelete(false) }}>Add correction</button>}
          {!confirmDelete && <button className="link agent-delete-link" onClick={() => { setConfirmDelete(true); setEditing(false) }}>Delete post</button>}
        </div>
      </div>
    </footer>
    {expanded && <div id={`replies-${value.id}`} className="agent-message-replies">
      {replies.length ? replies.map(reply => <div className={`agent-message-reply${reply.id === linkedReply?.id ? ' is-linked' : ''}`} key={reply.id}>
        <div className="agent-message-reply-head">
          <AgentProfile message={reply} statuses={statuses} monitorPaths={monitorPaths} />
          <CurrentStatus taskId={reply.task_id} statuses={statuses} />
        </div>
        <p>{reply.content}</p>
        <time dateTime={reply.time_posted}>{fmtDateTime(reply.time_posted)}</time>
      </div>) : <p className="muted small">No replies yet.</p>}
      {history === null && value.replies.length === 10
        ? <button className="link agent-older-replies" disabled={busy} onClick={() => void loadHistory()}>Browse older replies</button>
        : cursor && <button className="link agent-older-replies" disabled={busy} onClick={() => void loadHistory(cursor)}>Load older replies</button>}
    </div>}
    {editing && <div className="agent-message-form">
      <label className="field" htmlFor={`correction-${value.id}`}>
        <span>Correct a stale or inaccurate post</span>
      </label>
      <MessageComposer onSend={() => void correct()} sendDisabled={busy || !text.trim()}
        actions={<>
          <button type="button" className="ghost sm message-cancel" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          <MessageSendButton type="button" label="Publish correction" busy={busy} disabled={busy || !text.trim()} onClick={() => void correct()} />
        </>}>
        <textarea id={`correction-${value.id}`} value={text} maxLength={2000}
          disabled={busy} onChange={event => setText(event.target.value)} rows={3} />
      </MessageComposer>
    </div>}
    {confirmDelete && <div className="agent-message-delete-confirm">
      <p>Delete this post and hide its replies from the board? The records remain in the audit history.</p>
      <div className="row">
        <button className="sm danger" disabled={busy} onClick={() => void deletePost()}>Delete post</button>
        <button className="ghost sm" disabled={busy} onClick={() => setConfirmDelete(false)}>Cancel</button>
      </div>
    </div>}
    {error && <p className="note err" role="alert">{error}</p>}
  </article>
}

export default function AgentMessages({ jobs }: { jobs: BoardJob[] }) {
  const [params, setParams] = useSearchParams()
  const focusedId = params.get('thread')
  const focusedReplyId = params.get('reply')
  const [threads, setThreads] = useState<BoardThread[]>([])
  const [focusedThread, setFocusedThread] = useState<BoardThread | null>(null)
  const [focusedReply, setFocusedReply] = useState<BoardMessage | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async (next?: string) => {
    setLoading(true)
    setError('')
    try {
      const page = await api.get<Page<BoardThread>>('/eng/agent-messages',
        { limit: 20, ...(next ? { cursor: next } : {}) })
      setThreads(old => next ? [...old, ...page.items] : page.items)
      setCursor(page.next_cursor)
    } catch (caught) { setError((caught as Error).message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (!focusedId) { setFocusedThread(null); return }
    setFocusedThread(null)
    let cancelled = false
    void api.get<{ thread: BoardThread }>(`/eng/agent-messages/${encodeURIComponent(focusedId)}`)
      .then(result => { if (!cancelled) setFocusedThread(result.thread) })
      .catch(caught => { if (!cancelled) setError((caught as Error).message) })
    return () => { cancelled = true }
  }, [focusedId])
  useEffect(() => {
    if (!focusedId || !focusedReplyId) { setFocusedReply(null); return }
    setFocusedReply(null)
    let cancelled = false
    void api.get<{ reply: BoardMessage }>(`/eng/agent-messages/${encodeURIComponent(focusedId)}/replies/${encodeURIComponent(focusedReplyId)}`)
      .then(result => { if (!cancelled) setFocusedReply(result.reply) })
      .catch(caught => { if (!cancelled) setError((caught as Error).message) })
    return () => { cancelled = true }
  }, [focusedId, focusedReplyId])

  const statuses = new Map<string, string>()
  const monitorPaths = new Map<string, string>()
  for (const job of jobs) if (!statuses.has(job.task_id)) {
    statuses.set(job.task_id, job.status)
    monitorPaths.set(job.task_id, `/eng/agents/${encodeURIComponent(job.task_id)}/${encodeURIComponent(job.job_id)}`)
  }
  const shown = focusedThread && !threads.some(item => item.id === focusedThread.id)
    ? [focusedThread, ...threads] : threads
  const ordered = focusedId ? [...shown].sort((a, b) => Number(b.id === focusedId) - Number(a.id === focusedId)) : shown
  const update = (thread: BoardThread) => {
    setThreads(old => old.map(item => item.id === thread.id ? thread : item).sort((a, b) => b.activity_at.localeCompare(a.activity_at)))
    if (focusedThread?.id === thread.id) setFocusedThread(thread)
  }
  const remove = (id: string) => {
    setThreads(old => old.filter(item => item.id !== id))
    if (focusedThread?.id === id) setFocusedThread(null)
    if (focusedId === id) setFocusedReply(null)
    if (focusedId === id) setParams({}, { replace: true })
  }

  return <div className="agent-messages-page">
    {focusedId && <div className="agent-message-focus"><span>Showing linked message</span><Link to="/eng/agents/messages">Show all messages</Link></div>}
    {error && <p className="note err" role="alert">{error}</p>}
    {!loading && !ordered.length && <p className="experiments-empty">No messages yet.</p>}
    <section className="agent-message-grid" aria-label="Agent discussions">
      {ordered.map(thread => <Discussion key={thread.id} value={thread} statuses={statuses} monitorPaths={monitorPaths}
        focused={thread.id === focusedId} linkedReply={thread.id === focusedId ? focusedReply : null}
        onCorrected={update} onDeleted={remove} />)}
    </section>
    {cursor && <button className="sm" disabled={loading} onClick={() => void load(cursor)}>Load older posts</button>}
    {loading && <p className="muted small">Loading messages…</p>}
  </div>
}
